import React, { useEffect, useMemo, useState } from 'react';
import Loader from '../UI/Loader/Loader';
import PrimaryModal from '../UI/Modal/PrimaryModal';
import { Button } from '../UI';
import { useUserContext } from '../../contexts/UserContext';
import { createTenantActivityOffers } from '../../utils/APIs';
import { formattedTemplateName } from '../../utils/helper';
import { CheckCircle, XCircle } from 'lucide-react';
import AgGridWrapper from '../UI/AgGrid';
import { useContentfulContext } from '../../contexts/ContentfulContext';

const CreateBulkOfferModal = ({ experiences, hideCreateOfferModal }) => {
    const { userData } = useUserContext();
    const { enums, content } = useContentfulContext();

    const [rowData, setRowData] = useState(experiences?.length ? experiences.map((exp) => ({ ...exp, offerName: formattedTemplateName(exp.name) })) : []);
    const [gridApi, setGridApi] = useState(null);
    const [error, setError] = useState("");
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [response, setResponse] = useState({ result: [], errors: [] });

    const [colDefs, setColDefs] = useState([
        {
            headerName: content.BULK_ACTIVATION_CONTENT.adobe_target.create_offer_modal.exp_header,
            field: "name",
            filter: false,
            suppressMovable: true,
            cellRenderer: ({ data }) => {
                return data.name;
            },
        },
        {
            headerName: content.BULK_ACTIVATION_CONTENT.adobe_target.create_offer_modal.offer_header,
            field: 'offerName',
            sortable: false,
            editable: (params) => {
                return !params.data?.statusMessage?.startsWith("ID:");
            },
            filter: false,
            resizable: true,
            suppressMovable: true,
            suppressColumnVirtualisation: true,
            cellStyle: (params) => {
                const rowIndex = params.node.rowIndex;
                let classes = {};
                if (errors[rowIndex]?.["offerName"]) {
                    classes.border = "0.8px solid red";
                    classes.backgroundColor = "#ffe6e6";
                }
                return classes;
            },
        },
    ]);

    useEffect(() => {
        // Update colDefs based on the current errors state
        setColDefs((prevColDefs) => {
            return prevColDefs.map((col) => {
                if (col.field === "offerName") {
                    return {
                        ...col,
                        cellStyle: (params) => {
                            const rowIndex = params.node.rowIndex;
                            let classes = {};
                            if (errors[rowIndex]?.["offerName"]) {
                                classes.border = "0.8px solid red";
                                classes.backgroundColor = "#ffe6e6";
                            }
                            return classes;
                        },
                    };
                }
                return col;
            });
        });
    }, [errors]);

    const onCellValueChanged = (event) => {
        const { data, rowIndex } = event;
        setRowData((prevData) => {
            const updatedData = [...prevData];
            updatedData[rowIndex] = data;
            return updatedData;
        });
    };

    const onCellClicked = (params) => {
        const { node, colDef } = params;
        const rowIndex = node.rowIndex;
        const field = colDef.field;

        if (errors[rowIndex]?.[field]) {
            setError(`Error: ${errors[rowIndex][field]}`);
        } else {
            setError("");
        }
    };

    const onCellEditingStopped = (event) => {
        setError("");
        checkCellValidation(event.rowIndex, event.column.colId, event.value);
    };

    const checkCellValidation = (rowIndex, colId, value) => {
        let temp = JSON.parse(JSON.stringify(errors));
        if (!temp[rowIndex]) temp[rowIndex] = {};
        if (colId == "offerName") {
            if (!value) {
                temp[rowIndex]["offerName"] = content.BULK_ACTIVATION_CONTENT.adobe_target.create_offer_modal.errors.offer_required;
                setErrors(temp)
                return;
            }
        }
        temp[rowIndex]["offerName"] = "";
        delete temp[rowIndex]["offerName"];
        setErrors(temp);
    }

    const checkValidationAllCell = () => {
        const newErrors = {};
        let isValid = true;

        rowData.forEach((row, rowIndex) => {
            if (!newErrors[rowIndex]) newErrors[rowIndex] = {};

            if (!row["offerName"]) {
                newErrors[rowIndex]["offerName"] = content.BULK_ACTIVATION_CONTENT.adobe_target.create_offer_modal.errors.offer_required;
                isValid = false;
            }
        })
        if (!isValid)
            setError(content.BULK_ACTIVATION_CONTENT.adobe_target.create_offer_modal.errors.common_validation_msg)
        return isValid;
    }

    useEffect(() => {
        if (gridApi && colDefs.length > 2) {
            setTimeout(() => {
                gridApi.autoSizeAllColumns();
            }, 50);
        }
    }, [colDefs, gridApi]);

    const onGridReady = (params) => {
        setGridApi(params.api);
        params.api.autoSizeAllColumns();
    };

    const autoSizeStrategy = useMemo(() => ({ type: "fitGridWidth" }), [rowData, colDefs]);

    const updateRowDataWithResponse = (rowData, res) => {
        return rowData.map(row => {
            const successRecord = [...res.result, ...response.result].find(item => item.name === row.offerName);
            const errorRecord = res.errors.find(item => item.name === row.offerName);

            return {
                ...row,
                statusMessage: successRecord
                    ? `ID: ${successRecord.id}`
                    : errorRecord
                        ? `Error: ${errorRecord.message}`
                        : "Pending",
            };
        });
    };

    const handleOnSubmit = async () => {
        try {
            const isValidate = checkValidationAllCell();
            if (!isValidate) {
                return;
            }

            const offers = []

            for (const item of rowData) {
                const payload = {
                    name: item["offerName"],
                    experience_id: item._id,
                    destination: "adobe_target",
                    workspace: userData.defaultWorkspace?.workspace_id,
                    content: item?.templates[0]?.type === enums?.templateTypes.json_a_to_html.id ? item.experience_json : JSON.parse(item.experience_json),
                    type: item?.templates[0]?.type === enums?.templateTypes.json_a_to_html.id ? 'content' : 'json',
                }
                if (item?.templates[0]) {
                    if (!item?.statusMessage?.startsWith("ID:")) {
                        offers.push(payload);
                    }
                }
            }

            setLoading(true);
            const res = await createTenantActivityOffers(
                userData?.token,
                userData?.selectedOrganization?.org_token,
                {
                    tenant: userData.defaultAdobeIO?.tenant_name,
                    data: { offers }
                }
            );
            setError("");
            setResponse({
                result: [...response.result, ...res?.data?.result],
                errors: res?.data?.errors
            });
            // toast.success(`Offers are created in Adobe Target.`);

            const updatedData = updateRowDataWithResponse(rowData, res.data);
            setRowData(updatedData);

            const statusColumnExists = colDefs.some(col => col.field === "status");
            const statusMessageColumnExists = colDefs.some(col => col.field === "statusMessage");

            const newColDefs = JSON.parse(JSON.stringify(colDefs));
            if (!statusColumnExists) {
                newColDefs.unshift({
                    headerName: "",
                    field: "status",
                    maxWidth: 90,
                    sortable: false,
                    pinned: "left",
                    filter: false,
                    resizable: true,
                    suppressSizeToFit: true,
                    suppressColumnVirtualisation: true,
                    headerClass: "center-header ag-align-center-header w-full",
                    cellStyle: { textAlign: "center" },
                    cellRenderer: (params) => {
                        const hasError = params?.data?.statusMessage?.startsWith("ID:");
                        return (
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    height: "100%",
                                }}
                            >
                                {hasError ? (
                                    <CheckCircle color="green" size={24} />
                                ) : (
                                    <XCircle color="red" size={24} />
                                )}
                            </div>
                        );
                    },
                });
            }
            if (!statusMessageColumnExists) {
                newColDefs.push({
                    headerName: content.BULK_ACTIVATION_CONTENT.adobe_target.create_offer_modal.status_header,
                    field: "statusMessage",
                    cellStyle: params => ({
                        color: params.value.startsWith("ID:") ? "green" : "red"
                    }),
                    sortable: true,
                    editable: true,
                    filter: true,
                    resizable: true,
                    suppressMovable: true,
                    suppressColumnVirtualisation: true,
                });
            }
            if (!statusColumnExists || !statusMessageColumnExists) setColDefs(newColDefs);
        } catch (error) {
            setError(error?.message);
        } finally {
            setLoading(false);
        }
    }

    return (
        <PrimaryModal title="Create Offers" hideModal={hideCreateOfferModal} widthClass="max-w-4xl">
            {loading && <Loader />}
            <div className='p-6 space-y-6'>
                {rowData.length > 0 && (
                    <div
                        id="create-bulk-offer-id"
                        className="ag-theme-quartz w-full"
                    >
                        <AgGridWrapper
                            tableId="create-bulk-offer-id"
                            rowData={rowData}
                            columnDefs={colDefs}
                            autoSizeStrategy={autoSizeStrategy}
                            onGridReady={onGridReady}
                            suppressRowClickSelection
                            onCellClicked={onCellClicked}
                            onCellValueChanged={onCellValueChanged}
                            onCellEditingStopped={onCellEditingStopped}
                            stopEditingWhenCellsLoseFocus={true}
                        />
                        {response.result?.length !== rowData?.length && (
                            <>
                                <div className="flex mt-2">
                                    <p className="text-sm italic">{content.BULK_ACTIVATION_CONTENT.adobe_target.create_offer_modal.notes}</p>
                                </div>

                                <div className="space-y-2 mt-2 h-[20px]">
                                    <div className="flex flex-col">
                                        <p className="text-sm text-red-500">{error}</p>
                                    </div>
                                </div>

                                <div className="flex items-center justify-end mt-4">
                                    <Button variant="primary" loading={loading} onClick={handleOnSubmit}>{
                                        response.errors?.length > 0 ?
                                            content.BULK_ACTIVATION_CONTENT.adobe_target.create_offer_modal.try_again_cta :
                                            content.BULK_ACTIVATION_CONTENT.adobe_target.create_offer_modal.create_offer_cta
                                    }</Button>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
        </PrimaryModal>
    );
};

export default CreateBulkOfferModal;